import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useLocation, Link } from 'react-router-dom';

import { FeedbackForm } from '../types/index';
import { useAppSelector } from '../store/hooks';
import api from '../api';

import { FaHeadphones, FaClipboardList, FaUsers, FaChartLine, FaTrophy, FaMusic, FaRobot, FaCheckCircle } from 'react-icons/fa';
import { IoCheckmarkCircle } from 'react-icons/io5';
import { GiTargetShot, GiMeditation } from 'react-icons/gi';
import { PiBrainBold, PiWaveformFill, PiPlantFill } from 'react-icons/pi';
import { MdTimer } from 'react-icons/md';
import PublicHeader from '../components/layout/PublicHeader';
import { SiStreamrunners } from 'react-icons/si';

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user } = useAppSelector((state) => state.user);

  const [feedback, setFeedback] = useState<FeedbackForm>({ name: user?.name || '', email: user?.email || '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [showToast, setShowToast] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [heroLoaded, setHeroLoaded] = useState(false);

  useEffect(() => {
    if (user) {
      setFeedback(prev => ({
        ...prev,
        name: user.name || prev.name,
        email: user.email || prev.email
      }));
    }
  }, [user]);

  useEffect(() => {
    if (location.hash) {
      const element = document.getElementById(location.hash.replace("#", ""));

      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: "smooth" });
        }, 100); // small delay ensures DOM is ready
      }
    }
  }, [location]);


  const handleFeedbackSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMessage('');

    try {
      const payload: any = {
        name: feedback.name,
        message: feedback.message,
      };

      if (feedback.email && feedback.email.trim() !== '') {
        payload.email = feedback.email.trim();
      }

      await api.feedbacks.submitFeedback(payload);

      setShowToast(true);
      setFeedback({ name: user?.name || '', email: user?.email || '', message: '' });
      setTimeout(() => setShowToast(false), 3000);
    } catch (error: any) {
      console.error('Failed to submit feedback:', error);
      const errorMsg = error.response?.data?.message || error.message || 'Failed to submit feedback. Please try again.';
      setErrorMessage(errorMsg);
      setTimeout(() => setErrorMessage(''), 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const goToDashboard = () => {
    navigate('/dashboard');
  };

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  useEffect(() => {
    setHeroLoaded(true);
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  }, []);

  return (
    <>
      <Helmet>
        <html lang="en" />
        <title>Focuzon - A Quiet Space for Deep Work with Pomodoro Timer & Focus Sessions</title>
        <meta name="description" content="Focuzon is a quiet space for deep work and concentration. Free Pomodoro timer, focus sessions, team collaboration, and mindful breaks. Start your first focused work session today." />
        <meta name="keywords" content="focus app, quiet space, best focus app, deep work app, pomodoro timer, focus timer, concentration app, productivity app, focus sessions, team focus, workplace focus, mindfulness app, quiet workspace, distraction free, focus music, binaural beats, focus sounds, time management, work timer, study timer, focus tool, deep work tool, focus workspace, team collaboration, shared focus, focus analytics, focus tracker, meditation app, calm workspace, focus environment" />
        <meta name="author" content="Focuzon" />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        <link rel="canonical" href="https://focuzon.com/" />
        <meta name="language" content="English" />
        <meta name="revisit-after" content="7 days" />
        <meta name="distribution" content="global" />
        <meta name="rating" content="general" />
        <meta name="geo.region" content="US" />
        <meta name="geo.placename" content="United States" />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://focuzon.com/" />
        <meta property="og:title" content="Focuzon - A Quiet Space for Deep Work | Pomodoro Timer" />
        <meta property="og:description" content="Focuzon is a quiet space for deep work and concentration. Free Pomodoro timer, focus sessions, team collaboration, and mindful breaks." />
        <meta property="og:image" content="https://focuzon.com/og-image.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="Focuzon - Focus App for Deep Work" />
        <meta property="og:site_name" content="Focuzon" />
        <meta property="og:locale" content="en_US" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://focuzon.com/" />
        <meta name="twitter:title" content="Focuzon - A Quiet Space for Deep Work | Pomodoro Timer" />
        <meta name="twitter:description" content="Focuzon is a quiet space for deep work and concentration. Free Pomodoro timer, focus sessions, team collaboration, and mindful breaks." />
        <meta name="twitter:image" content="https://focuzon.com/og-image.jpg" />
        <meta name="twitter:image:alt" content="Focuzon - Focus App for Deep Work" />
        <meta name="twitter:site" content="@focuzon" />
        <meta name="twitter:creator" content="@focuzon" />

        {/* Structured Data - SoftwareApplication */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "Focuzon",
            "alternateName": "Focus Mode AI",
            "description": "Focuzon is a quiet space for deep work and concentration. Free Pomodoro timer, focus sessions, team collaboration, and mindful breaks designed around how focus actually works.",
            "url": "https://focuzon.com",
            "applicationCategory": ["ProductivityApplication", "BusinessApplication"],
            "operatingSystem": ["Web", "iOS", "Android"],
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD",
              "availability": "https://schema.org/InStock"
            },
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.8",
              "ratingCount": "1250",
              "bestRating": "5",
              "worstRating": "1"
            },
            "featureList": [
              "Pomodoro Timer",
              "Focus Sessions",
              "Team Collaboration",
              "Focus Sounds & Music",
              "Mindful Breaks",
              "Session Notes",
              "Focus Analytics",
              "Calming Activities"
            ],
            "screenshot": "https://focuzon.com/og-image.jpg",
            "softwareVersion": "1.0",
            "releaseNotes": "A quiet workspace for deep, focused work",
            "browserRequirements": "Requires JavaScript. Requires HTML5.",
            "softwareHelp": {
              "@type": "CreativeWork",
              "url": "https://focuzon.com/about"
            }
          })}
        </script>

        {/* Structured Data - Organization */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "Focuzon",
            "alternateName": "Focus Mode AI",
            "url": "https://focuzon.com",
            "logo": "https://focuzon.com/focuzon.png",
            "description": "Focuzon provides a quiet workspace for deep, focused work. We build tools designed around how focus actually works.",
            "foundingDate": "2024",
            "contactPoint": {
              "@type": "ContactPoint",
              "email": "support@focuzon.com",
              "contactType": "Customer Support"
            },
            "sameAs": [
              "https://twitter.com/focuzon",
              "https://github.com/focuzon"
            ],
            "address": {
              "@type": "PostalAddress",
              "addressCountry": "US"
            }
          })}
        </script>

        {/* Structured Data - FAQPage */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "What is Focuzon?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Focuzon is a focus app designed for deep work and concentration. It combines Pomodoro timer functionality, focus sessions, team collaboration, and mindful breaks in one calm workspace."
                }
              },
              {
                "@type": "Question",
                "name": "Is Focuzon free?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, Focuzon is completely free to use. Start your first focus session today without any cost."
                }
              },
              {
                "@type": "Question",
                "name": "How does Focuzon help with focus?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Focuzon helps you focus by providing a quiet workspace with Pomodoro timers, focus sounds, mindful breaks, and team collaboration features. It's designed around how focus actually works, not productivity hype."
                }
              },
              {
                "@type": "Question",
                "name": "Can I use Focuzon with my team?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, Focuzon supports team collaboration with shared focus sessions. Work together in aligned focus time without meetings or noise."
                }
              },
              {
                "@type": "Question",
                "name": "What makes Focuzon different from other focus apps?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Focuzon is designed as a quiet workspace, not a productivity system. We focus on calm, sustained attention rather than gamification, badges, or competitive elements. It's meditation-retreat-meets-workspace."
                }
              }
            ]
          })}
        </script>

        {/* Structured Data - WebSite */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebSite",
            "name": "Focuzon",
            "alternateName": "Focus Mode AI",
            "url": "https://focuzon.com",
            "description": "A quiet space for deep work and concentration",
            "inLanguage": "en-US",
            "potentialAction": {
              "@type": "SearchAction",
              "target": "https://focuzon.com/search?q={search_term_string}",
              "query-input": "required name=search_term_string"
            }
          })}
        </script>

        {/* Structured Data - BreadcrumbList */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": "https://focuzon.com/"
              }
            ]
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-[#FFF8F0] relative overflow-hidden" itemScope itemType="https://schema.org/WebPage">
        {/* Enhanced Orange Gradient Background Shapes */}
        <div className="fixed inset-0 pointer-events-none z-0">
          <div className="absolute top-0 -left-1/4 w-96 h-96 bg-gradient-to-br from-orange-300/50 to-orange-200/40 rounded-full blur-3xl animate-blob-slow"></div>
          <div className="absolute top-1/4 -right-1/4 w-96 h-96 bg-gradient-to-br from-orange-400/50 to-orange-300/40 rounded-full blur-3xl animate-blob-slow" style={{ animationDelay: "5s" }}></div>
          <div className="absolute bottom-0 left-1/4 w-80 h-80 bg-gradient-to-br from-orange-200/40 to-orange-100/30 rounded-full blur-3xl animate-blob-slow" style={{ animationDelay: "10s" }}></div>
          <div className="absolute top-1/2 right-1/3 w-72 h-72 bg-gradient-to-br from-orange-300/40 to-orange-200/30 rounded-full blur-3xl animate-blob-slow" style={{ animationDelay: "15s" }}></div>
        </div>

        <PublicHeader />

        {/* Hero Section - Focus-First Workspace */}
        <section id="what" className="relative z-10 pt-40 pb-48 min-h-screen flex items-center bg-[#FFF8F0] overflow-hidden" itemScope itemType="https://schema.org/WebPageElement">
          {/* Animated Background Elements */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute top-[20%] left-[10%] w-64 h-64 bg-orange-200/40 rounded-full mix-blend-multiply filter blur-3xl opacity-60 animate-blob"></div>
            <div className="absolute top-[30%] right-[10%] w-72 h-72 bg-amber-200/40 rounded-full mix-blend-multiply filter blur-3xl opacity-60 animate-blob" style={{ animationDelay: "2s" }}></div>
            <div className="absolute -bottom-8 left-[30%] w-80 h-80 bg-rose-200/40 rounded-full mix-blend-multiply filter blur-3xl opacity-60 animate-blob" style={{ animationDelay: "4s" }}></div>

            {/* Floating Shapes */}
            <div className="absolute top-[15%] right-[20%] w-16 h-16 border-4 border-orange-300/20 rounded-full animate-float opacity-50"></div>
            <div className="absolute bottom-[20%] left-[15%] w-12 h-12 border-4 border-amber-300/20 rounded-full animate-float opacity-50" style={{ animationDelay: "2s" }}></div>

            {/* Additional Elements */}
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] border border-orange-100/30 rounded-full animate-pulse-slow pointer-events-none"></div>
            <div className="absolute top-1/3 left-1/4 w-2 h-2 bg-orange-400/40 rounded-full animate-float" style={{ animationDelay: "1.5s" }}></div>
            <div className="absolute top-2/3 right-1/3 w-3 h-3 bg-amber-400/40 rounded-full animate-float" style={{ animationDelay: "3.5s" }}></div>
            <div className="absolute bottom-1/4 right-1/4 w-2 h-2 bg-rose-400/40 rounded-full animate-float" style={{ animationDelay: "0.5s" }}></div>
          </div>

          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1
              className={`text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-12 text-gray-900 leading-tight uppercase transition-all duration-700 ease-out ${heroLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-[12px]'
                }`}
            >
              A Digital Workspace Built for Deep Focus
            </h1>

            <div className="flex flex-wrap items-center justify-center gap-2.5 mt-8 text-base sm:text-lg text-gray-600">
              <span
                className={`transition-all duration-700 ease-out delay-[120ms] ${heroLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-[12px]'
                  }`}
              >
                All your
              </span>
              <span
                className={`px-4 py-2 rounded-full border border-orange-400/50 bg-orange-50/5 text-orange-600/90 text-sm font-medium hover:opacity-80 transition-all duration-700 ease-out delay-[150ms] ${heroLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-[12px]'
                  }`}
              >
                Focus sessions
              </span>
              <span
                className={`px-4 py-2 rounded-full border border-blue-400/50 bg-blue-50/5 text-blue-600/90 text-sm font-medium hover:opacity-80 transition-all duration-700 ease-out delay-[200ms] ${heroLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-[12px]'
                  }`}
              >
                Session notes
              </span>
              <span
                className={`px-4 py-2 rounded-full border border-violet-400/50 bg-violet-50/5 text-violet-600/90 text-sm font-medium hover:opacity-80 transition-all duration-700 ease-out delay-[250ms] ${heroLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-[12px]'
                  }`}
              >
                Shared focus
              </span>
              <span
                className={`px-4 py-2 rounded-full border border-teal-400/50 bg-teal-50/5 text-teal-600/90 text-sm font-medium hover:opacity-80 transition-all duration-700 ease-out delay-[300ms] ${heroLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-[12px]'
                  }`}
              >
                Focus sounds
              </span>
              <span
                className={`px-4 py-2 rounded-full border border-amber-400/50 bg-amber-50/5 text-amber-600/90 text-sm font-medium hover:opacity-80 transition-all duration-700 ease-out delay-[350ms] ${heroLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-[12px]'
                  }`}
              >
                Breaks
              </span>
              <span
                className={`px-4 py-2 rounded-full border border-emerald-400/50 bg-emerald-50/5 text-emerald-600/90 text-sm font-medium hover:opacity-80 transition-all duration-700 ease-out delay-[400ms] ${heroLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-[12px]'
                  }`}
              >
                Insights
              </span>
              <span
                className={`transition-all duration-700 ease-out delay-[450ms] ${heroLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-[12px]'
                  }`}
              >
                thoughtfully combined in one calm, private workspace.
              </span>
            </div>

            {/* Hero CTA - Try Focuzon Free */}
            <div className={`mt-12 flex justify-center transition-all duration-700 ease-out delay-[500ms] ${heroLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-[12px]'}`}>
              <button
                onClick={() => (isAuthenticated ? goToDashboard() : navigate('/signup'))}
                className="text-orange-600 hover:text-orange-700 font-medium text-base border-b border-orange-400/50 hover:border-orange-500 pb-0.5 transition-colors"
                aria-label="Try Focuzon free"
              >
                Try Focuzon Free
              </button>
            </div>

            {/* Scroll Indicator */}
            <div
              className={`absolute bottom-10 left-1/2 transform -translate-x-1/2 cursor-pointer transition-all duration-1000 delay-[800ms] ${heroLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                }`}
              onClick={() => scrollToSection('why')}
            >
              <div className="w-[35px] h-[64px] rounded-3xl border-4 border-orange-400/30 flex justify-center items-start p-2">
                <div className="w-1.5 h-1.5 bg-orange-400 rounded-full animate-bounce mb-1"></div>
              </div>
            </div>
          </div>
        </section>

        {/* Why Section */}
        <section id="why" className="relative z-10 py-24 bg-white" itemScope itemType="https://schema.org/WebPageElement">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl sm:text-5xl font-medium mb-6 text-gray-900">
                Designed Around How Focus Actually Works
              </h2>
              <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
                A calmer way to work: built to support deep attention, healthy pacing, and meaningful collaboration.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  icon: <FaRobot className="text-3xl text-orange-500" />,
                  title: 'Focus, Guided Naturally',
                  description: 'Smart session flow and gentle break reminders: designed to protect your attention, not interrupt it.'
                },
                {
                  icon: <MdTimer className="text-3xl text-orange-500" />,
                  title: 'Work at Your Own Rhythm',
                  description: 'Choose session lengths that fit how you think: short sprints, long deep work, or custom focus cycles.'
                },
                {
                  icon: <FaUsers className="text-3xl text-orange-500" />,
                  title: 'Focus Alone or Together',
                  description: 'Enter shared focus sessions with your team: no meetings, no noise, just aligned work time.'
                }
              ].map((item, index) => (
                <div
                  key={index}
                  className="text-center p-10 sm:p-12 bg-orange-50/30 rounded-2xl border border-orange-100/60 hover:-translate-y-1 hover:shadow-sm transition-all duration-300 ease-out"
                >
                  <div className="flex justify-center mb-6">{item.icon}</div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">{item.title}</h3>
                  <p className="text-gray-600 leading-relaxed text-sm sm:text-base">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How It Works Section */}
        <section id="how" className="relative z-10 py-24 bg-orange-50/30" itemScope itemType="https://schema.org/HowTo">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl sm:text-5xl font-medium mb-6 text-gray-900">
                Entering Focus, Step by Step
              </h2>
              <p className="text-lg sm:text-xl text-gray-600">Get started in three simple moments.</p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  step: '1',
                  title: 'Name What Matters',
                  description: 'Set a clear intention for this session: work, study, or creative thinking.',
                  icon: <GiTargetShot className="text-4xl text-orange-500" />
                },
                {
                  step: '2',
                  title: 'Choose Your Focus Window',
                  description: 'Select a session length that fits your rhythm: short sprints, deep stretches, or custom time.',
                  icon: <MdTimer className="text-4xl text-orange-500" />
                },
                {
                  step: '3',
                  title: 'Settle Into Focus',
                  description: 'Start your session and let Focuzon support you with focus sounds, gentle breaks, and reminders.',
                  icon: <SiStreamrunners className="text-4xl text-orange-500" />
                }
              ].map((item, index) => (
                <div
                  key={index}
                  className={`text-center bg-white rounded-2xl p-10 sm:p-12 border border-orange-100/60 transition-all duration-700 ease-out ${heroLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-[12px]'
                    }`}
                  style={{ transitionDelay: `${150 + (index * 100)}ms` }}
                >
                  <div className="inline-flex items-center justify-center w-20 h-20 bg-orange-50/50 rounded-full mb-6">
                    {item.icon}
                  </div>
                  <div className="text-orange-500 font-semibold text-xl mb-3">Step {item.step}</div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-5">{item.title}</h3>
                  <p className="text-gray-600 leading-relaxed text-sm sm:text-base">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section id="features" className="relative z-10 py-24 bg-white" itemScope itemType="https://schema.org/ItemList">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl sm:text-5xl font-medium mb-6 text-gray-900">
                Support for Deep, Focused Work
              </h2>
              <p className="text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto">
                A calm set of capabilities that support focus without distraction.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                {
                  icon: <FaHeadphones className="text-3xl text-orange-500" />,
                  title: 'Focus Sounds',
                  description: 'A quiet environment with nature sounds, binaural beats, or your own music: designed to support concentration.'
                },
                {
                  icon: <GiMeditation className="text-3xl text-orange-500" />,
                  title: 'Smart Breaks',
                  description: 'Gentle reminders for your eyes, posture, and mind: helping you maintain focus without interruption.'
                },
                {
                  icon: <FaClipboardList className="text-3xl text-orange-500" />,
                  title: 'Session Notes',
                  description: 'Capture thoughts and ideas as they arise: integrated naturally into your focus flow.'
                },
                {
                  icon: <FaUsers className="text-3xl text-orange-500" />,
                  title: 'Shared Focus',
                  description: 'Enter aligned work time with your team: shared focus sessions without meetings or noise.'
                },
                {
                  icon: <FaChartLine className="text-3xl text-orange-500" />,
                  title: 'Focus Insights',
                  description: 'Quiet observation of your focus patterns: understanding your rhythm without pressure or metrics.'
                },
                {
                  icon: <GiMeditation className="text-3xl text-orange-500" />,
                  title: 'Mindful Practices, Not Distractions',
                  description: 'Simple tools to reset your attention: breathing exercises, gentle puzzles, and calming interactions. Use them when helpful, ignore them when not. No competition, no pressure.'
                }
              ].map((feature, index) => (
                <div key={index} className="bg-orange-50/30 rounded-xl p-8 border border-orange-100/60 hover:-translate-y-1 hover:shadow-sm transition-all duration-300 ease-out">
                  <div className="mb-5">{feature.icon}</div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">{feature.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section - SEO Optimized */}
        <section id="faq" className="relative z-10 py-24 bg-white" itemScope itemType="https://schema.org/FAQPage">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl sm:text-5xl font-medium mb-6 text-gray-900">
                Frequently Asked Questions
              </h2>
              <p className="text-lg sm:text-xl text-gray-600">
                Everything you need to know about Focuzon
              </p>
            </div>

            <div className="space-y-6">
              <article className="bg-orange-50/30 rounded-xl p-8 border border-orange-100/60">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  What is Focuzon?
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  Focuzon is a calm digital workspace built for deep work. It combines structured focus sessions, Pomodoro timers, ambient focus sounds, mindful breaks, and optional team rooms, all designed to help you concentrate without distraction or pressure.
                </p>
              </article>

              <article className="bg-orange-50/30 rounded-xl p-8 border border-orange-100/60">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Is Focuzon free?
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  Focuzon follows a freemium model. Core focus tools will always remain free, and you can start using them immediately, no credit card required. In the future, optional premium features will be available for users who want advanced capabilities.
                </p>
              </article>

              <article className="bg-orange-50/30 rounded-xl p-8 border border-orange-100/60">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  How does Focuzon improve focus?
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  Focuzon structures your work into intentional focus blocks, reduces decision fatigue, and creates a distraction free environment. With built-in timers, ambient sounds, and guided breaks, it helps you sustain attention and avoid burnout.
                </p>
              </article>

              <article className="bg-orange-50/30 rounded-xl p-8 border border-orange-100/60">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Can I use Focuzon with my team?
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  Team rooms and shared focus sessions are currently in development. We’re carefully building collaboration features that protect deep work instead of interrupting it. Individual focus is fully available today, with team capabilities launching soon.
                </p>
              </article>

              <article className="bg-orange-50/30 rounded-xl p-8 border border-orange-100/60">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  What devices does Focuzon support?
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  Focuzon works entirely in your web browser and is built as a Progressive Web App (PWA). You can use it on desktop, tablet, or mobile and install it like a native app if you prefer.
                </p>
              </article>

              <article className="bg-orange-50/30 rounded-xl p-8 border border-orange-100/60">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Will there be premium features?
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  Yes. While essential focus tools will always remain free, we plan to introduce premium add-ons such as advanced analytics, enhanced team capabilities, and additional customization options for users who want more control and insights.
                </p>
              </article>

            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="relative z-10 py-32 bg-[#FFF8F0]">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-4xl sm:text-5xl font-medium mb-6 text-gray-900">
              Start Your First Focus Session
            </h2>
            <p className="text-lg sm:text-xl text-gray-600 mb-12 max-w-2xl mx-auto leading-relaxed">
              Experience what deep, focused work feels like: without setup, pressure, or distractions.
            </p>
            <div className="flex flex-col items-center gap-4">
              <button
                onClick={goToDashboard}
                className="bg-orange-600 text-white px-10 py-4 rounded-lg text-lg font-medium hover:bg-orange-700 transition-colors duration-200"
                aria-label="Start a focus session with Focuzon"
              >
                Start a Focus Session
              </button>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section id="contact" className="relative z-10 py-24 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl sm:text-4xl font-medium text-center mb-4 text-gray-900">
              Help Shape Focuzon
            </h2>
            <p className="text-center text-gray-600 mb-12 text-lg">
              If you have thoughts, ideas, or quiet frustrations about focus, we're listening.
            </p>

            <div className="bg-[#FFF8F0] rounded-xl p-10 border border-orange-100/40">
              <form onSubmit={handleFeedbackSubmit} className="space-y-6">
                <div className="grid sm:grid-cols-2 gap-4">
                  <input
                    type="text"
                    placeholder="How should we address you?"
                    value={feedback.name}
                    onChange={(e) => setFeedback({ ...feedback, name: e.target.value })}
                    className="w-full px-4 py-3 bg-white border border-orange-200/50 rounded-lg focus:ring-2 focus:ring-orange-400/40 focus:border-orange-400/70 focus:outline-none text-gray-950 placeholder:text-gray-500 placeholder:opacity-50 transition-all duration-200"
                    required
                  />
                  <input
                    type="email"
                    placeholder="Email (only if you'd like a reply)"
                    value={feedback.email}
                    onChange={(e) => setFeedback({ ...feedback, email: e.target.value })}
                    className="w-full px-4 py-3 bg-white border border-orange-200/50 rounded-lg focus:ring-2 focus:ring-orange-400/40 focus:border-orange-400/70 focus:outline-none text-gray-950 placeholder:text-gray-500 placeholder:opacity-50 transition-all duration-200"
                  />
                </div>
                <textarea
                  placeholder="What's on your mind? A small idea, a rough thought, or something that broke your focus today."
                  rows={6}
                  value={feedback.message}
                  onChange={(e) => setFeedback({ ...feedback, message: e.target.value })}
                  className="w-full px-5 py-4 bg-white border border-orange-200/50 rounded-lg focus:ring-2 focus:ring-orange-400/40 focus:border-orange-400/70 focus:outline-none resize-none text-gray-950 placeholder:text-gray-500 placeholder:opacity-50 transition-all duration-200"
                  required
                ></textarea>
                {errorMessage && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm">
                    {errorMessage}
                  </div>
                )}
                <div className="space-y-3">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-orange-600 text-white px-6 py-3 rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-orange-700 transition-colors duration-200"
                  >
                    {isSubmitting ? 'Sending...' : 'Share Your Thoughts'}
                  </button>
                  <p className="text-center text-sm text-gray-500">We read every message. No automated replies.</p>
                </div>
              </form>
            </div>
          </div>
        </section>

        {/* Toast Notification for Feedback */}
        {showToast && (
          <div className="fixed bottom-4 left-4 z-50 bg-gradient-to-r from-orange-500 to-orange-600 text-white px-6 py-4 rounded-lg shadow-lg flex items-center gap-2">
            <IoCheckmarkCircle className="text-white text-xl" />
            <span>Feedback submitted successfully!</span>
          </div>
        )}

      </div>
    </>
  );
};

export default LandingPage;
